import React from "react";
import { Brain, Clock, TrendingUp, Shield, Users, DollarSign, Zap } from "lucide-react";

const Benefits = () => {
  const benefits = [
    {
      icon: <Brain className="w-8 h-8 text-indigo-500" />,
      title: "Autonomy",
      description: "Move beyond automation to intelligence that thinks and acts independently",
      bullets: ["Self-learning AI", "One-click remediation", "Continuous improvement"],
      metric: "24/7 Active"
    },
    {
      icon: <Zap className="w-8 h-8 text-yellow-500" />,
      title: "Speed",
      description: "Faster remediation with automated workflows and intelligent prioritization",
      bullets: ["10x faster triage", "Automatic ticket creation", "Real-time escalation"],
      metric: "10x Faster"
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-500" />,
      title: "Protection",
      description: "Critical vulnerabilities prioritized and fixed before attackers exploit them",
      bullets: ["Risk-based triage", "Exploitability analysis", "Proactive defense"],
      metric: "100%"
    },
    {
      icon: <Users className="w-8 h-8 text-purple-500" />,
      title: "Accountability",
      description: "Every issue auto-assigned, tracked, and escalated with clear ownership",
      bullets: ["SLA monitoring", "Automated follow-ups", "Risk register visibility"],
      metric: "100% Tracked"
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-green-500" />,
      title: "Efficiency", 
      description: "Reduce wasted effort by eliminating duplicates and false positives",
      bullets: ["90% less noise", "Auto-deduplicate findings", "Smart vendor engagement"],
      metric: "90%"
    },
    {
      icon: <DollarSign className="w-8 h-8 text-emerald-500" />,
      title: "ROI",
      description: "Maximize value from existing tools and prove security program impact",
      bullets: ["Tool performance metrics", "Cost optimization", "Executive dashboards"],
      metric: "Tool ROI Analytics"
    }
  ];

  return (
    <section id="benefits" className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Transform Your Security
            <span className="text-blue-600 block">Operations Today</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Experience immediate improvements across every aspect of your security program 
            with measurable results that leadership can see and appreciate.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {benefits.map((benefit, index) => (
            <div 
              key={index}
              className="group bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 bg-gray-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50 transition-colors duration-300">
                  {benefit.icon}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">{benefit.metric}</div>
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {benefit.title}
              </h3>
              
              <p className="text-base font-medium text-gray-700 mb-3">
                {benefit.description}
              </p>
              
              {benefit.bullets ? (
                <ul className="space-y-2 text-gray-600">
                  {benefit.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-blue-600 mr-2">•</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-600 leading-relaxed">
                  {benefit.detail}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Results Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Real Results Our Customers Achieve
            </h3>
            <p className="text-base text-gray-600">
              Join organizations already transforming their security operations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">40%</div>
              <div className="text-gray-700 font-medium">Less Time on False Positives</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">10x</div>
              <div className="text-gray-700 font-medium">Faster Vulnerability Response</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">Risk-Based</div>
              <div className="text-gray-700 font-medium">Prioritized Issues</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">No More</div>
              <div className="text-gray-700 font-medium">Time Wasted on Manual Follow-ups</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;